import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Users, FileCheck, Download, Key, CreditCard, CheckCircle2, ArrowRight, AlertTriangle, RefreshCw, TrendingUp, Calendar } from 'lucide-react';
import { WelcomeBanner } from '../components/WelcomeBanner';
import { PluginUpdateNotification } from '../components/PluginUpdateNotification';

interface CustomerData {
  email: string;
  plan: string;
  current_usage: number;
  created_at: string;
}

interface SiteStats {
  totalCustomers: number;
  totalOptimizations: number;
  licenseKey: string | null;
}

interface SubscriptionStatus {
  license_key: string;
  tier: string;
  status: string;
  subscription: {
    status: string;
    auto_renewal_enabled: boolean;
    next_billing_date: string;
    days_until_renewal: number;
    annual_fee: number;
    cancellation_scheduled: boolean;
    cancels_at: string | null;
  };
  grace_period: {
    in_grace_period: boolean;
    grace_period_ends_at: string | null;
    days_left: number;
  };
  usage: {
    current_month: number;
    total: number;
    limit: number;
    remaining: number;
    percentage_used: number;
  };
  contact: {
    email: string;
    name: string;
  };
}

export function SiteOwnerDashboard() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<SiteStats>({
    totalCustomers: 0,
    totalOptimizations: 0,
    licenseKey: null,
  });
  const [customers, setCustomers] = useState<CustomerData[]>([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingBillingPortal, setLoadingBillingPortal] = useState(false);
  const [changingPlan, setChangingPlan] = useState<string | null>(null);
  const [showWelcomeBanner, setShowWelcomeBanner] = useState(false);
  const [userName, setUserName] = useState('');
  const [refreshingStatus, setRefreshingStatus] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, [profile]);

  const loadDashboardData = async () => {
    if (!profile) return;

    try {
      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('full_name, first_login_completed')
        .eq('id', profile.id)
        .maybeSingle();

      if (userProfile) {
        setUserName(userProfile.full_name || 'there');
        if (!userProfile.first_login_completed) {
          setShowWelcomeBanner(true);
        }
      }

      let siteOwner = await supabase
        .from('site_owners')
        .select('id, contact_email, user_id')
        .eq('user_id', profile.id)
        .maybeSingle()
        .then(res => res.data);

      if (!siteOwner && profile.email) {
        const { data: siteOwnerByEmail } = await supabase
          .from('site_owners')
          .select('id, contact_email, user_id')
          .eq('contact_email', profile.email)
          .is('user_id', null)
          .maybeSingle();

        if (siteOwnerByEmail) {
          const { error: linkError } = await supabase
            .from('site_owners')
            .update({ user_id: profile.id, updated_at: new Date().toISOString() })
            .eq('id', siteOwnerByEmail.id);

          if (!linkError) {
            siteOwner = { ...siteOwnerByEmail, user_id: profile.id };
            console.log('Automatically linked site_owner to user account');
          }
        }
      }

      let licenseKey = null;
      let licenseId = null;
      if (siteOwner) {
        const { data: license } = await supabase
          .from('plugin_licenses')
          .select('id, license_key')
          .eq('site_owner_id', siteOwner.id)
          .maybeSingle();

        licenseKey = license?.license_key || null;
        licenseId = license?.id || null;

        if (licenseKey) {
          await fetchSubscriptionStatus(licenseKey);
        }
      }

      let customersData = [];
      let customersCount = 0;
      let jobsCount = 0;

      if (licenseId) {
        const { data: customersResult, error: customersError } = await supabase
          .from('wordpress_subscriptions')
          .select('email, plan, current_usage, created_at')
          .eq('license_id', licenseId)
          .order('created_at', { ascending: false })
          .limit(10);

        if (customersError) throw customersError;
        customersData = customersResult || [];

        const { count: totalCustomersCount } = await supabase
          .from('wordpress_subscriptions')
          .select('*', { count: 'exact', head: true })
          .eq('license_id', licenseId);

        customersCount = totalCustomersCount || 0;

        const { count: totalJobsCount } = await supabase
          .from('wordpress_jobs')
          .select('*', { count: 'exact', head: true })
          .eq('license_id', licenseId);

        jobsCount = totalJobsCount || 0;
      }

      setStats({
        totalCustomers: customersCount,
        totalOptimizations: jobsCount,
        licenseKey: licenseKey,
      });

      setCustomers(customersData);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptionStatus = async (licenseKey: string) => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/site-owner-subscription-status?license_key=${licenseKey}`,
        {
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch subscription status');
      }

      const data = await response.json();
      setSubscriptionStatus(data);
    } catch (error) {
      console.error('Error fetching subscription status:', error);
    }
  };

  const handleManageBilling = async () => {
    if (!stats.licenseKey) return;

    setLoadingBillingPortal(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/site-owner-billing-portal`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ licenseKey: stats.licenseKey }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to create billing portal session');
      }

      const data = await response.json();
      window.location.href = data.url;
    } catch (error) {
      console.error('Error opening billing portal:', error);
      alert('Failed to open billing portal. Please try again.');
    } finally {
      setLoadingBillingPortal(false);
    }
  };

  const handlePlanChange = async (newPlanType: string) => {
    if (!stats.licenseKey) return;

    setChangingPlan(newPlanType);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/site-owner-change-plan`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            licenseKey: stats.licenseKey,
            newPlanType: newPlanType,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to change plan');
      }

      const data = await response.json();
      alert(`Success! ${data.message}\n\nYour subscription has been updated and prorated charges have been applied.`);

      await fetchSubscriptionStatus(stats.licenseKey);
    } catch (error) {
      console.error('Error changing plan:', error);
      alert(`Failed to change plan: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setChangingPlan(null);
    }
  };

  const handleRefreshStatus = async () => {
    if (!stats.licenseKey) return;

    setRefreshingStatus(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/refresh-license-status`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ licenseKey: stats.licenseKey }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to refresh status');
      }

      const data = await response.json();

      if (data.updated) {
        alert(`Status updated! ${data.message}`);
      } else {
        alert('Status is already up to date');
      }

      await fetchSubscriptionStatus(stats.licenseKey);
    } catch (error) {
      console.error('Error refreshing status:', error);
      alert(`Failed to refresh status: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setRefreshingStatus(false);
    }
  };

  const downloadPlugin = () => {
    const link = document.createElement('a');
    link.href = 'https://ptfvgyrfluuxgnjnlsps.supabase.co/storage/v1/object/public/plugin-releases/resume-doctor-ai-v6.3.0.zip';
    link.download = 'resume-doctor-ai-v6.3.0.zip';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const getPlanDisplayName = (tier: string) => {
    const plans: Record<string, string> = {
      'free': 'Basic',
      'basic': 'Basic',
      'pro': 'Professional',
      'professional': 'Professional',
      'enterprise': 'Enterprise',
    };
    return plans[tier.toLowerCase()] || tier;
  };

  const getSubscriptionStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      'active': 'bg-green-100 text-green-800',
      'past_due': 'bg-red-100 text-red-800',
      'canceled': 'bg-gray-100 text-gray-800',
      'incomplete': 'bg-yellow-100 text-yellow-800',
      'trialing': 'bg-blue-100 text-blue-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">My Site Dashboard</h1>
          <p className="mt-2 text-gray-600">Manage your Resume Doctor AI installation</p>
        </div>

        {showWelcomeBanner && (
          <WelcomeBanner
            userName={userName}
            onDismiss={() => setShowWelcomeBanner(false)}
          />
        )}

        <PluginUpdateNotification currentVersion="6.4.0" />

        {subscriptionStatus?.grace_period.in_grace_period && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-6 rounded-lg shadow-md">
            <div className="flex items-start">
              <AlertTriangle className="w-6 h-6 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-lg font-bold text-red-900 mb-2">Payment Failed - Action Required</h3>
                <p className="text-red-800 mb-3">
                  Your subscription payment has failed. You have{' '}
                  <span className="font-bold">{subscriptionStatus.grace_period.days_left} days</span> remaining
                  to update your payment method before your license is suspended.
                </p>
                <button
                  onClick={handleManageBilling}
                  disabled={loadingBillingPortal}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50"
                >
                  {loadingBillingPortal ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Loading...</span>
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4" />
                      <span>Update Payment Method</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {subscriptionStatus?.subscription.cancellation_scheduled && (
          <div className="mb-6 bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-lg shadow-md">
            <div className="flex items-start">
              <AlertTriangle className="w-6 h-6 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-lg font-bold text-yellow-900 mb-2">Subscription Scheduled for Cancellation</h3>
                <p className="text-yellow-800 mb-3">
                  Your subscription will be canceled on{' '}
                  <span className="font-bold">
                    {subscriptionStatus.subscription.cancels_at
                      ? new Date(subscriptionStatus.subscription.cancels_at).toLocaleDateString()
                      : 'N/A'}
                  </span>.
                  You will retain access until that date.
                </p>
                <button
                  onClick={handleManageBilling}
                  disabled={loadingBillingPortal}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors font-medium disabled:opacity-50"
                >
                  {loadingBillingPortal ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Loading...</span>
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4" />
                      <span>Reactivate Subscription</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {!stats.licenseKey && (
          <div className="mb-8 bg-gradient-to-r from-blue-600 to-emerald-600 rounded-2xl p-8 text-white shadow-xl">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <CreditCard className="w-8 h-8" />
                  <h2 className="text-2xl font-bold">Get Your License Today</h2>
                </div>
                <p className="text-blue-100 text-lg mb-4">
                  Purchase a license to unlock the Resume Doctor AI WordPress plugin and start earning from your website.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-300" />
                    <span className="text-blue-50">Easy WordPress integration</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-300" />
                    <span className="text-blue-50">Instant download after purchase</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-300" />
                    <span className="text-blue-50">Multiple plan options available</span>
                  </li>
                </ul>
              </div>
              <div className="flex-shrink-0">
                <button
                  onClick={() => navigate('/site-owner-payment')}
                  className="bg-white text-blue-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-blue-50 transition-all duration-300 hover:scale-105 shadow-lg flex items-center gap-3"
                >
                  <span>Purchase License</span>
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        )}

        {subscriptionStatus && (
          <div className="mb-8 bg-gradient-to-br from-blue-50 to-emerald-50 rounded-2xl p-8 shadow-lg border border-blue-200">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {getPlanDisplayName(subscriptionStatus.tier)} Plan
                </h2>
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getSubscriptionStatusColor(subscriptionStatus.subscription.status)}`}>
                    {subscriptionStatus.subscription.status.replace('_', ' ').toUpperCase()}
                  </span>
                  <span className="text-gray-600">
                    ${subscriptionStatus.subscription.annual_fee}/year
                  </span>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleRefreshStatus}
                  disabled={refreshingStatus}
                  className="inline-flex items-center gap-2 px-4 py-3 bg-white border-2 border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 transition-all duration-300 font-medium disabled:opacity-50"
                  title="Sync with Stripe to get latest status"
                >
                  {refreshingStatus ? (
                    <>
                      <RefreshCw className="w-5 h-5 animate-spin" />
                      <span>Syncing...</span>
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-5 h-5" />
                      <span>Refresh Status</span>
                    </>
                  )}
                </button>
                <button
                  onClick={handleManageBilling}
                  disabled={loadingBillingPortal}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-emerald-600 text-white rounded-lg hover:shadow-xl transition-all duration-300 hover:scale-105 font-bold disabled:opacity-50"
                >
                  {loadingBillingPortal ? (
                    <>
                      <RefreshCw className="w-5 h-5 animate-spin" />
                      <span>Loading...</span>
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5" />
                      <span>Manage Billing</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  <p className="text-sm font-medium text-gray-600">Next Billing Date</p>
                </div>
                <p className="text-lg font-bold text-gray-900">
                  {subscriptionStatus.subscription.next_billing_date
                    ? new Date(subscriptionStatus.subscription.next_billing_date).toLocaleDateString()
                    : 'N/A'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {subscriptionStatus.subscription.days_until_renewal} days remaining
                </p>
              </div>

              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-5 h-5 text-emerald-600" />
                  <p className="text-sm font-medium text-gray-600">Monthly Usage</p>
                </div>
                <p className="text-lg font-bold text-gray-900">
                  {subscriptionStatus.usage.current_month} / {subscriptionStatus.usage.limit}
                </p>
                <div className="mt-2 bg-gray-200 rounded-full h-2 overflow-hidden">
                  <div
                    className={`h-full transition-all duration-500 ${
                      subscriptionStatus.usage.percentage_used >= 90
                        ? 'bg-red-500'
                        : subscriptionStatus.usage.percentage_used >= 70
                        ? 'bg-yellow-500'
                        : 'bg-emerald-500'
                    }`}
                    style={{ width: `${Math.min(subscriptionStatus.usage.percentage_used, 100)}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {subscriptionStatus.usage.remaining} remaining
                </p>
              </div>

              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <RefreshCw className="w-5 h-5 text-blue-600" />
                  <p className="text-sm font-medium text-gray-600">Auto-Renewal</p>
                </div>
                <p className="text-lg font-bold text-gray-900">
                  {subscriptionStatus.subscription.auto_renewal_enabled ? 'Enabled' : 'Disabled'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {subscriptionStatus.subscription.auto_renewal_enabled
                    ? 'Your subscription will renew automatically'
                    : 'Please enable auto-renewal in billing settings'}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Customers</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats.totalCustomers}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Optimizations</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats.totalOptimizations}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-lg">
                <FileCheck className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">License Status</p>
                <p className="text-lg font-bold text-gray-900 mt-2">
                  {stats.licenseKey ? 'Active' : 'Inactive'}
                </p>
              </div>
              <div className={`p-3 rounded-lg ${stats.licenseKey ? 'bg-green-100' : 'bg-red-100'}`}>
                <Key className={`w-6 h-6 ${stats.licenseKey ? 'text-green-600' : 'text-red-600'}`} />
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">License Information</h2>
            {stats.licenseKey ? (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                    <p className="font-semibold text-green-900">Active License</p>
                  </div>
                  <p className="text-sm text-green-700">Your license is active and ready to use</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Your License Key</p>
                  <div className="p-3 bg-gradient-to-r from-blue-50 to-emerald-50 rounded-lg border-2 border-blue-200 font-mono text-sm break-all">
                    {stats.licenseKey}
                  </div>
                </div>
                <button
                  onClick={downloadPlugin}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-4 bg-gradient-to-r from-blue-600 to-emerald-600 text-white rounded-lg hover:shadow-xl transition-all duration-300 hover:scale-105 font-bold"
                >
                  <Download className="w-5 h-5" />
                  <span>Download Plugin v6.3.0</span>
                </button>
                <p className="text-xs text-gray-500 text-center">Latest version • Updated Feb 8, 2026</p>
              </div>
            ) : (
              <div className="text-center py-8">
                <Key className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-700 font-medium mb-2">No License Yet</p>
                <p className="text-sm text-gray-500 mb-4">Purchase a license to get started</p>
                <button
                  onClick={() => navigate('/site-owner-payment')}
                  className="inline-flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  <CreditCard className="w-4 h-4" />
                  <span>Purchase Now</span>
                </button>
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Installation Guide</h2>
            <div className="space-y-3 text-sm text-gray-600">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">1</div>
                <div>
                  <p className="font-medium text-gray-900">Download the plugin</p>
                  <p>Click the download button to get the latest version</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">2</div>
                <div>
                  <p className="font-medium text-gray-900">Upload to WordPress</p>
                  <p>Go to Plugins → Add New → Upload Plugin</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">3</div>
                <div>
                  <p className="font-medium text-gray-900">Enter license key</p>
                  <p>Activate the plugin and enter your license key</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {subscriptionStatus && (
          <div className="mb-8 bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Change Your Plan</h2>
            <p className="text-gray-600 mb-6">
              Upgrade or downgrade your subscription to match your needs
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className={`relative border-2 rounded-xl p-6 transition-all ${
                (subscriptionStatus.tier.toLowerCase() === 'basic' || subscriptionStatus.tier.toLowerCase() === 'free')
                  ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                  : 'border-gray-200 hover:border-blue-300'
              }`}>
                {(subscriptionStatus.tier.toLowerCase() === 'basic' || subscriptionStatus.tier.toLowerCase() === 'free') && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-bold">
                      CURRENT PLAN
                    </span>
                  </div>
                )}
                <h3 className="text-xl font-bold text-gray-900 mb-2">Basic</h3>
                <p className="text-3xl font-bold text-gray-900 mb-1">$297<span className="text-lg text-gray-600">/year</span></p>
                <p className="text-sm text-gray-600 mb-4">Perfect for small sites</p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>100 monthly resume optimizations</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>AI-powered optimization engine</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>ATS compatibility scoring</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Email support</span>
                  </li>
                </ul>
                {subscriptionStatus.tier.toLowerCase() !== 'basic' && (
                  <button
                    onClick={() => handlePlanChange('basic')}
                    disabled={changingPlan !== null}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {changingPlan === 'basic' ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        <span>Updating...</span>
                      </>
                    ) : (
                      'Switch to Basic'
                    )}
                  </button>
                )}
              </div>

              <div className={`relative border-2 rounded-xl p-6 transition-all ${
                (subscriptionStatus.tier.toLowerCase() === 'professional' || subscriptionStatus.tier.toLowerCase() === 'pro')
                  ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200'
                  : 'border-gray-200 hover:border-emerald-300'
              }`}>
                {(subscriptionStatus.tier.toLowerCase() === 'professional' || subscriptionStatus.tier.toLowerCase() === 'pro') && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-emerald-600 text-white px-3 py-1 rounded-full text-xs font-bold">
                      CURRENT PLAN
                    </span>
                  </div>
                )}
                <h3 className="text-xl font-bold text-gray-900 mb-2">Professional</h3>
                <p className="text-3xl font-bold text-gray-900 mb-1">$597<span className="text-lg text-gray-600">/year</span></p>
                <p className="text-sm text-gray-600 mb-4">Great for growing sites</p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>500 monthly resume optimizations</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>AI-powered optimization engine</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>ATS compatibility scoring</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Priority email support</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Advanced analytics dashboard</span>
                  </li>
                </ul>
                {(subscriptionStatus.tier.toLowerCase() !== 'professional' && subscriptionStatus.tier.toLowerCase() !== 'pro') && (
                  <button
                    onClick={() => handlePlanChange('professional')}
                    disabled={changingPlan !== null}
                    className="w-full px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {changingPlan === 'professional' ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        <span>Updating...</span>
                      </>
                    ) : (
                      (subscriptionStatus.tier.toLowerCase() === 'basic' || subscriptionStatus.tier.toLowerCase() === 'free') ? 'Upgrade to Professional' : 'Switch to Professional'
                    )}
                  </button>
                )}
              </div>

              <div className={`relative border-2 rounded-xl p-6 transition-all ${
                subscriptionStatus.tier.toLowerCase() === 'enterprise'
                  ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                  : 'border-gray-200 hover:border-blue-300'
              }`}>
                {subscriptionStatus.tier.toLowerCase() === 'enterprise' && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-bold">
                      CURRENT PLAN
                    </span>
                  </div>
                )}
                <h3 className="text-xl font-bold text-gray-900 mb-2">Enterprise</h3>
                <p className="text-3xl font-bold text-gray-900 mb-1">$997<span className="text-lg text-gray-600">/year</span></p>
                <p className="text-sm text-gray-600 mb-4">For high-traffic sites</p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Unlimited monthly resume optimizations</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>AI-powered optimization engine</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>ATS compatibility scoring</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Priority support with dedicated success manager</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Full white-label customization</span>
                  </li>
                </ul>
                {subscriptionStatus.tier.toLowerCase() !== 'enterprise' && (
                  <button
                    onClick={() => handlePlanChange('enterprise')}
                    disabled={changingPlan !== null}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {changingPlan === 'enterprise' ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        <span>Updating...</span>
                      </>
                    ) : (
                      'Upgrade to Enterprise'
                    )}
                  </button>
                )}
              </div>
            </div>

            <p className="mt-6 text-sm text-gray-600 text-center">
              Plan changes will be prorated automatically. Click the button to manage your subscription in the billing portal.
            </p>
          </div>
        )}

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">Recent Customers</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usage</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Joined</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {customers.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                      No customers yet. Start promoting your site!
                    </td>
                  </tr>
                ) : (
                  customers.map((customer, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{customer.email}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800 capitalize">
                          {customer.plan}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{customer.current_usage}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(customer.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
